import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Button,
  Fade,
  Container,
  alpha,
  Divider,
  Card,
  CardContent,
  CardMedia,
  Tooltip,
  Rating,
  Stack,
  CardActions,
} from "@mui/material";
import {
  School,
  TrendingUp,
  Favorite,
  Star,
  CheckCircle,
  Pending,
  Checklist,
  Person,
  CalendarToday,
  Assignment,
  EmojiEvents,
  ShowChart,
  Download,
  Print,
  PhotoCamera,
  Description,
  Psychology,
  Grade,
  AccessTime,
  LocationOn,
  Visibility,
  Share,
  ThumbUp,
  Comment,
  ExpandMore,
  ExpandLess,
  Palette,
  MusicNote,
  SportsSoccer,
  Science,
  LocalLibrary,
  Group,
  Photo,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  AreaChart,
  Area,
  CartesianGrid,
} from "recharts";
import apiEndpoints from "../apiconfig";
import { keyframes } from "@mui/system";

const API_URL = apiEndpoints.progress;


// Floating animation
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

// Modern color palette matching activities page
const COLORS = {
  primary: "#FF6B6B",
  secondary: "#4ECDC4",
  tertiary: "#FFD166",
  quaternary: "#06D6A0",
  dark: "#1a1a1a",
  light: "rgba(255, 255, 255, 0.7)",
};

const Progress = () => {
  const [favorite, setFavorite] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedCard, setExpandedCard] = useState(null);

const [studentGuid, setStudentGuid] = useState(null);

useEffect(() => {
  const raw = localStorage.getItem("active_student");
  const guid = raw ? JSON.parse(raw).guid : null;
  setStudentGuid(guid);
}, []);

  /* ================= FETCH ================= */
  useEffect(() => {
    if (!studentGuid) return;

    (async () => {
      try {
        const token = localStorage.getItem("parent_token");
        const res = await fetch(`${API_URL}?student_guid=${studentGuid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!json.status) throw new Error(json.message);
        setApiData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [studentGuid]);

  if (loading)
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
            url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <Typography variant="h6" color="text.secondary">
        Loading progress data...</Typography>
      </Box>
    );

  if (error)
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
            url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );

  const { student, stats, subjects, timeline, attendance_performance } =
    apiData;
  const attendancePerf = attendance_performance;

const aggregateSkills = (subjects = []) => {
  const map = {};

  subjects.forEach((item) => {
    if (!Array.isArray(item.skills)) return;

    item.skills.forEach((skill) => {
      if (!skill) return;
      map[skill] = (map[skill] || 0) + 1;
    });
  });

  return Object.entries(map).map(([name, count]) => ({
    name,
    count,
  }));
};


  const skillsGained = aggregateSkills(subjects);

  // Chart Data
  const progressData = [
    { name: "Overall", value: stats.overall_progress, color: COLORS.primary },
    { name: "Attendance", value: stats.attendance, color: COLORS.secondary },
    { name: "Tasks", value: stats.task_completion, color: COLORS.tertiary },
  ];

  const activityData = subjects.map((a) => ({
    name: a.name.length > 10 ? a.name.slice(0, 10) + "…" : a.name,
    marks: a.marks,
    date: a.date || "N/A",
  }));

 const attendanceTrend =
   apiData.attendance_timeline?.map((item) => ({
     date: item.month,
     attendance: item.progress,
   })) || [];


  // Activity categories for visual consistency
  const activityCategories = [
    {
      id: "academic",
      label: "Academic Performance",
      color: "#FF6B6B",
      icon: <TrendingUp />,
    },
    {
      id: "attendance",
      label: "Attendance",
      color: "#4ECDC4",
      icon: <CalendarToday />,
    },
    {
      id: "skills",
      label: "Skills Developed",
      color: "#FFD166",
      icon: <Checklist />,
    },
    {
      id: "activities",
      label: "Activities",
      color: "#06D6A0",
      icon: <Assignment />,
    },
  ];

  const handleExpand = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const getCategory = (id) =>
    activityCategories.find((c) => c.id === id) || activityCategories[0];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
          url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 100,
          right: 100,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(120, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 100,
          left: 100,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* HEADER */}
        <Fade in timeout={800}>
          <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.125rem" },
                  }}
                >
                  Student Progress Dashboard
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  Comprehensive analysis of your child's academic performance,
                  attendance, and skill development
                </Typography>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* QUICK STATS - Matching activities page style */}
        <Fade in timeout={900}>
          <Grid container spacing={3} sx={{ mb: 4, px: { xs: 2, md: 4 } }}>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#FF6B6B", mb: 1 }}
                >
                  {stats.overall_progress}%
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Overall Progress
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#4ECDC4", mb: 1 }}
                >
                  {stats.attendance}%
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Attendance Rate
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#FFD166", mb: 1 }}
                >
                  {stats.task_completion}%
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Tasks Completed
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 900, color: "#06D6A0", mb: 1 }}
                >
                  {Math.round(
                    subjects.reduce((acc, s) => acc + (s.progress || 0), 0) /
                      subjects.length
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Average Score
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* STUDENT PROFILE CARD - Matching activities style */}
        <Fade in timeout={1000}>
          <Box sx={{ px: { xs: 2, md: 4 }, mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(20px)",
                border: `1px solid ${alpha("#FF6B6B", 0.2)}`,
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: alpha("#FF6B6B", 0.05),
                  borderBottom: `1px solid ${alpha("#FF6B6B", 0.1)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "16px",
                        bgcolor: alpha("#FF6B6B", 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FF6B6B",
                        animation: `${floatAnimation} 2s ease-in-out infinite`,
                      }}
                    >
                      <Person sx={{ fontSize: 40 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#1a1a1a",
                          mb: 0.5,
                          flexWrap: "wrap", // ✅ allows chips to flow properly
                        }}
                      >
                        {student.name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Chip
                          icon={<School />}
                          label={student.class}
                          size="small"
                          sx={{
                            maxWidth: "100%",
                            px: 1.5,
                            height: 28,
                            bgcolor: alpha("#FF6B6B", 0.1),
                            color: "#FF6B6B",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            "& .MuiChip-label": {
                              px: 0.5,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setFavorite(!favorite)}
                    sx={{
                      color: favorite ? "#FF6B6B" : "#666",
                    }}
                  >
                    <Favorite />
                  </IconButton>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Overall Progress
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#FF6B6B" }}
                    >
                      {stats.overall_progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.overall_progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha("#FF6B6B", 0.1),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        </Fade>

        {/* CHARTS SECTION */}
        <Fade in timeout={1100}>
          <Grid container spacing={3} sx={{ mb: 4, px: { xs: 2, md: 4 } }}>
            {/* Performance Metrics */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha("#4ECDC4", 0.2)}`,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    <TrendingUp sx={{ mr: 1, verticalAlign: "middle" }} />
                    Performance Metrics
                  </Typography>
                  <Chip
                    label="Current"
                    size="small"
                    sx={{ bgcolor: alpha("#4ECDC4", 0.1) }}
                  />
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={progressData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar
                      dataKey="value"
                      radius={[8, 8, 0, 0]}
                      fill={COLORS.primary}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Attendance Trend */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha("#06D6A0", 0.2)}`,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    <ShowChart sx={{ mr: 1, verticalAlign: "middle" }} />
                    Attendance Trend
                  </Typography>
                  <Chip
                    label="Weekly"
                    size="small"
                    sx={{ bgcolor: alpha("#06D6A0", 0.1) }}
                  />
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={attendanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area
                      type="monotone"
                      dataKey="attendance"
                      stroke={COLORS.quaternary}
                      fill={alpha(COLORS.quaternary, 0.1)}
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* SKILLS & ACTIVITIES SECTION */}
        <Fade in timeout={1200}>
          <Grid container spacing={3} sx={{ px: { xs: 2, md: 4 }, mb: 4 }}>
            {/* Skills Gained */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha("#FFD166", 0.2)}`,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    <Checklist sx={{ mr: 1, verticalAlign: "middle" }} />
                    Skills Developed
                  </Typography>
                  <Chip
                    label={`${skillsGained.length} skills`}
                    size="small"
                    sx={{ bgcolor: alpha("#FFD166", 0.1) }}
                  />
                </Box>
                {skillsGained.length === 0 ? (
                  <Typography color="text.secondary">
                    No skills data available
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {skillsGained.map((skill, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 2,
                          borderRadius: "12px",
                          bgcolor: alpha("#FFD166", 0.05),
                          border: `1px solid ${alpha("#FFD166", 0.1)}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            bgcolor: alpha("#FFD166", 0.1),
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Typography fontWeight={500}>{skill.name}</Typography>
                        <Chip
                          label={`x${skill.count}`}
                          size="small"
                          sx={{
                            bgcolor: alpha("#FFD166", 0.2),
                            color: "#FFD166",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Recent Activities */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha("#7209B7", 0.2)}`,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    <Assignment sx={{ mr: 1, verticalAlign: "middle" }} />
                    Recent Activities
                  </Typography>
                  <Chip
                    label={`${subjects.length} activities`}
                    size="small"
                    sx={{ bgcolor: alpha("#7209B7", 0.1) }}
                  />
                </Box>
                <Stack spacing={2}>
                  {subjects.slice(0, 4).map((activity, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        flexWrap: "wrap",
                        borderRadius: "12px",
                        bgcolor: "rgba(255, 255, 255, 0.5)",
                        border: "1px solid rgba(0, 0, 0, 0.05)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.8)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {activity.name}
                        </Typography>

                        <Rating
                          value={activity.rating || 0}
                          readOnly
                          size="small"
                        />

                        <Typography variant="body2" color="text.secondary">
                          Score: {activity.progress}/100 •{" "}
                          {activity.completed ? "Completed" : "In Progress"}
                        </Typography>
                      </Box>

                      <Chip
                        icon={
                          activity.completed ? <CheckCircle /> : <Pending />
                        }
                        label={activity.completed ? "Completed" : "In Progress"}
                        size="small"
                        sx={{
                          bgcolor: activity.completed
                            ? alpha("#06D6A0", 0.1)
                            : alpha("#FFD166", 0.1),
                          color: activity.completed ? "#06D6A0" : "#FFD166",
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* DETAILED PERFORMANCE - Expandable Cards */}
        <Fade in timeout={1300}>
          <Box sx={{ px: { xs: 2, md: 4 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                p: 3,
                mb: 4,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 3, color: "#333" }}
              >
                📊 Detailed Performance Analysis
              </Typography>

              <Grid container spacing={3}>
                {subjects.map((subject, index) => {
                  const category = getCategory("academic");
                  const isExpanded = expandedCard === index;

                  return (
                    <Grid item xs={12} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "16px",
                          background: "rgba(255, 255, 255, 0.7)",
                          backdropFilter: "blur(20px)",
                          border: `1px solid ${alpha(category.color, 0.2)}`,
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                          },
                        }}
                      >
                        {/* Subject Header */}
                        <Box
                          sx={{
                            p: 3,
                            bgcolor: alpha(category.color, 0.05),
                            borderBottom: `1px solid ${alpha(
                              category.color,
                              0.1
                            )}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "12px",
                                  bgcolor: alpha(category.color, 0.1),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: category.color,
                                }}
                              >
                                {category.icon}
                              </Box>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 600, color: "#1a1a1a" }}
                                >
                                  {subject.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#666" }}
                                >
                                  Score: {subject.marks}/100 •{" "}
                                  {subject.completed
                                    ? "Completed"
                                    : "In Progress"}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              onClick={() => handleExpand(index)}
                              sx={{
                                transform: isExpanded
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                              }}
                            >
                              <ExpandMore />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Progress Bar */}
                        <Box sx={{ p: 3, pt: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: "#666" }}>
                              Progress
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: category.color }}
                            >
                              {subject.progress}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={subject.progress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: alpha(category.color, 0.1),
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                                backgroundColor: category.color,
                              },
                            }}
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        </Fade>

        {/* ATTENDANCE PERFORMANCE */}
        <Fade in timeout={1400}>
          <Box sx={{ px: { xs: 2, md: 4 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(20px)",
                border: `1px solid ${alpha("#4ECDC4", 0.2)}`,
                p: 3,
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  <CalendarToday sx={{ mr: 1, verticalAlign: "middle" }} />
                  Attendance Performance
                </Typography>
                <Chip
                  icon={<CheckCircle />}
                  label={`${attendancePerf?.percentage || 0}% Attendance`}
                  color={
                    attendancePerf?.percentage > 75 ? "success" : "warning"
                  }
                />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      background: "rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Present Days:{" "}
                      <strong>{attendancePerf?.present_days ?? 0}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Absent Days:{" "}
                      <strong>{attendancePerf?.absent_days ?? 0}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Total Working Days:{" "}
                      <strong>{attendancePerf?.total_days ?? 0}</strong>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={attendancePerf?.percentage || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha("#4ECDC4", 0.1),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 4,
                          backgroundColor: "#4ECDC4",
                        },
                      }}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      background: "rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(0, 0, 0, 0.05)",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1, color: "#333" }}
                    >
                      Attendance Insight
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {attendancePerf?.total_days === 0
                        ? "Attendance records are not available yet."
                        : attendancePerf?.percentage >= 90
                        ? "Excellent attendance consistency. Keep it up!"
                        : attendancePerf?.percentage >= 75
                        ? "Good attendance. Try to minimize absences."
                        : "Attendance needs improvement for better learning continuity."}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>

        {/* FOOTER */}
        <Fade in timeout={1500}>
          <Box sx={{ py: 4, textAlign: "center", px: { xs: 2, md: 4 } }}>
            <Typography variant="body2" sx={{ color: "#666", opacity: 0.8 }}>
              © 2025 My School • Progress data updated daily • Last updated:{" "}
              {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Progress;
